import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import BuyChart from '../../../components/Chart/BuyChart';
import SaleChart from '../../../components/Chart/SaleChart';
import Big from 'big-js';
import { parseNumber } from '../../../utils/number';
import { getCountVolumeFunctions } from '../../../utils/volumes/countVolumes';
import CardRowCard from '../../../components/CardRow/CardRowCard';
import { BuyInputs } from './BuyInputs';
import PropTypes from 'prop-types';
import { useFindProductById, useFormManupilation, useProductManipulation, useSelectedProduct, useValidation } from '../hooks/contractBuy';
import { BuyFooter } from './footer/BuyFooter';
import { BuyDialog } from './modal/BuyDialog';
import { useShowGeneraSnackbar } from '../../../components/Snackbar/SnackbarManager';
import { useFetchProducts } from '../hooks/api/productsApi';
import LoadingIndicator from '../../../components/LoadingIndicator/LoadingIndicator';
import { useFetchContractProductQuantities, useGetContractPurchasePrices } from '../hooks/api/contractsApi';
import { ErrorPage } from '../../Error/ErrorPage';
import { SuccessSnackBar } from './SuccessSnackBar';
import { useLocation } from 'react-router';
import CardRow from '../../../components/CardRow/CardRow';
import { PurchaseSimulation } from '../components/charts/PurchaseSimulation';
import { FormattedMessage } from 'react-intl';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';
import useAppContext from '../../../hooks/context/useAppContext';
import { volumeBigToNumber } from '../../../utils/volumes';

const LoaderHolder = styled.div`
  height: 4px;
  width: 100%;
`;

const BuyLayout = styled.div`
  margin-top: 16px;
  .inputAndGraphCard {
    display: flex;
    padding: 47px 37px 34px;
    .chart {
      flex: 1;
    }
    .inputBox {
      flex: 1;
    }
  }
`;

const StyledBuyChart = styled(BuyChart)`
  @media all and (width <= 650px) {
    display: none;
  }
`;

const StyledSaleChart = styled(SaleChart)`
  @media all and (width <= 650px) {
    display: none;
  }
`;

const StyledBuySimulationContainer = styled.div`
  @media all and (width <= 650px) {
    display: none;
  }
`;

export const CONTRACT_BUY_INTL_PREFIX = 'app.contract.buy';
export const PERCENT_ASKING_NAME = 'percentAsking';
export const MWH_ASKING_NAME = 'mwhAsking';
export const MW_ASKING_NAME = 'mwAsking';
export const PRODUCT_CHART_NAME = 'productChart';
export const PURCHASE_SIMULATION_CHART_NAME = 'purchaseSimulation';
export const PRODUCT_NAME = 'product';
export const RELEASING_CHECKBOX = 'releasing';
export const COMPLETE_HIDDEN_BUY_INFO_NAME = 'buyInfo';
export const GENERAL_ERROR_KEY = 'general';
export const MANUAL_PRICE_NAME = 'manualPrice';
export const MANUAL_RATE_NAME = 'manualRate';

const TradeFinishing = ({
  confirmModalOpen,
  setConfirmModalOpen,
  clearBuyVolumes,
  reloadContract,
  setValue,
  refreshQuantities,
  remainingPurchaseCount,
  showSnackbarSuccess,
  contractData,
  product,
  releasing,
  manualPrice,
  manualRate,
  purchaseInfo,
  showSnackbarGeneralError,
  validate,
  refreshProducts,
  user,
  sendNotification,
}) => {
  return (
    <>
      <BuyDialog
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onBuy={() => {
          setConfirmModalOpen(false);
          clearBuyVolumes();
          reloadContract();
          setValue(PRODUCT_NAME, undefined);
          refreshQuantities();
          showSnackbarSuccess(<SuccessSnackBar link={`/contract/${contractData.id}`} />);
        }}
        contract={contractData}
        product={product}
        releasing={releasing}
        manualPrice={manualPrice}
        manualRate={manualRate}
        purchaseInfo={purchaseInfo}
        sendNotification={sendNotification}
        onError={() => {
          setConfirmModalOpen(false);
          showSnackbarGeneralError();
        }}
      />
      <BuyFooter
        onBuy={async () => {
          const { success } = validate();
          if (success) {
            setConfirmModalOpen(true);
          }
        }}
        buyInfo={purchaseInfo}
        onRefresh={refreshProducts}
        contract={contractData}
        product={product}
        releasing={releasing}
        manualPrice={manualPrice}
        manualRate={manualRate}
        role={user.role}
        remainingPurchaseCount={remainingPurchaseCount}
      />
    </>
  );
};

TradeFinishing.propTypes = {
  confirmModalOpen: PropTypes.bool,
  setConfirmModalOpen: PropTypes.func,
  clearBuyVolumes: PropTypes.func,
  reloadContract: PropTypes.func,
  setValue: PropTypes.func,
  refreshQuantities: PropTypes.func,
  remainingPurchaseCount: PropTypes.number,
  showSnackbarSuccess: PropTypes.func,
  contractData: PropTypes.object,
  product: PropTypes.object,
  releasing: PropTypes.bool,
  manualPrice: PropTypes.number,
  manualRate: PropTypes.number,
  purchaseInfo: PropTypes.object,
  showSnackbarGeneralError: PropTypes.any,
  validate: PropTypes.func,
  refreshProducts: PropTypes.func,
  user: PropTypes.object,
  sendNotification: PropTypes.bool,
};

const setInitialValues = ({ products, product, quantity, year, setValue, countByMWh, onPurchaseInfoChanged, getFilteredContractedVolume }) => {
  const output = '';
  if (products && product) {
    if (products.filter((productItem) => productItem.id === product).length === 1) {
      setValue(PRODUCT_NAME, product);
      if (quantity) {
        setValue(MWH_ASKING_NAME, quantity);
        onPurchaseInfoChanged(countByMWh(new Big(quantity), getFilteredContractedVolume(), year), MWH_ASKING_NAME);
      }
    }
  }
  return output;
};

const getTranslationBase = (isFix) => {
  return isFix ? 'app.contract.sale' : 'app.contract.purchase';
};

const getChart = (isFix) => {
  return isFix ? StyledSaleChart : StyledBuyChart;
};

const getInitialProduct = (productFromLocation) => {
  const selectedProduct = sessionStorage.getItem('buyProduct');
  return selectedProduct != null ? parseInt(selectedProduct) : productFromLocation;
};

const ContractBuy = ({ contractData, getContract }) => {
  const TRANSLATION_BASE = getTranslationBase(contractData.isFix);
  const Chart = getChart(contractData.isFix);
  const { register, setValue, control, getValues, watch } = useForm();
  const location = useLocation();
  const year = contractData.contractPeriod;
  const purchaseMethod = contractData.purchaseMethod.id;
  const remainingPurchaseCount = contractData.remainingPurchaseCount;
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [manualPrice, setManualPrice] = useState(undefined);
  const [manualRate, setManualRate] = useState(undefined);
  const [isSaleAvailable, setIsSaleAvailable] = useState(false);
  const { products, refreshProducts, loading: productsLoading, error: productsError } = useFetchProducts(contractData);
  const { quantities, refreshQuantities, loading: quantitiesLoading, error: quantitiesError } = useFetchContractProductQuantities(contractData.id);
  const { data: purchasePrices, getContractPurchasePrices } = useGetContractPurchasePrices();
  const { getFilteredContractedVolume, getFilteredVolumes } = useProductManipulation(getValues, quantities, products);
  const { countByMW, countByMWh, countByPercent, countMaxAllowed } = useMemo(() => getCountVolumeFunctions(purchaseMethod), [purchaseMethod]);
  const { user } = useAppContext();
  const [productQuantity, setProductQuantity] = useState();
  const [sendNotification, setSendNotification] = useState(true);

  const reloadContract = () => {
    getContract(contractData.id);
  };

  useEffect(() => {
    sessionStorage.removeItem('buyProduct');
  }, []);

  useEffect(() => {
    setValue(PRODUCT_CHART_NAME, productQuantity);
  }, [setValue, productQuantity]);

  useEffect(() => {
    if (quantities != null) {
      const totalQuantity = quantities.filter((quantity) => quantity.productType !== 'REMAINING');
      setProductQuantity(totalQuantity);
    }
  }, [quantities]);

  const { findProductById } = useFindProductById(products);
  const { showSnackbarGeneralError, showSnackbarSuccess } = useShowGeneraSnackbar();
  const { purchaseInfo, setPurchaseInfo, onPurchaseInfoChanged } = useFormManupilation(productQuantity, setValue);

  useEffect(() => {
    getContractPurchasePrices(contractData.id);
  }, [getContractPurchasePrices, contractData.id]);

  useEffect(() => {
    setInitialValues({
      products,
      product: getInitialProduct(location?.state?.product),
      quantity: location?.state?.quantity,
      year,
      setValue,
      countByMWh,
      onPurchaseInfoChanged,
      getFilteredContractedVolume,
    });
  }, [setValue, products, location, countByMWh, getFilteredContractedVolume, onPurchaseInfoChanged, year]);

  useEffect(() => {
    const isSettlementPresent = !!products?.find((product) => product.priceSource.id === 'SETTLEMENT');
    products && setIsSaleAvailable(contractData.isSaleAvailable && !isSettlementPresent);
  }, [products, contractData.isSaleAvailable]);

  const { product } = useSelectedProduct(products, watch);
  const { validate, errors } = useValidation(product, getValues, countMaxAllowed, getFilteredVolumes, releasing, year);

  useEffect(() => {
    if (purchasePrices) {
      const simulationData = {
        currency: purchasePrices?.currency,
        prices: purchasePrices?.prices,
        currentPrice: product?.price?.price,
        buying: purchaseInfo?.buyingProduct?.quantities,
        manualPrice: manualPrice,
        manualRate: manualRate,
      };

      setValue(PURCHASE_SIMULATION_CHART_NAME, simulationData);
    }
  }, [purchaseInfo, product, purchasePrices, setValue, manualPrice, manualRate]);

  const onFillVolumeClicked = () => {
    const filteredVolumes = getFilteredVolumes(getValues[PRODUCT_CHART_NAME]);
    const maxPurchaseInfo = countMaxAllowed(filteredVolumes, year, releasing);
    if (maxPurchaseInfo.buyTotalsInfo.volumeMWh > 0) {
      onPurchaseInfoChanged(maxPurchaseInfo);
    }
  };

  const onNumberBuyInputChange = (inputName, event) => {
    const value = parseNumber(event.target.value);
    if (value === undefined || value === 0) {
      clearBuyVolumes();
      return;
    }

    const bigValue = new Big(value);

    switch (inputName) {
      case MWH_ASKING_NAME:
        onPurchaseInfoChanged(countByMWh(bigValue, getFilteredContractedVolume(), year, releasing), inputName);
        break;
      case MW_ASKING_NAME:
        onPurchaseInfoChanged(countByMW(bigValue, getFilteredContractedVolume(), year, releasing), inputName);
        break;
      case PERCENT_ASKING_NAME:
        onPurchaseInfoChanged(countByPercent(bigValue.div(100), getFilteredContractedVolume(), year, releasing), inputName);
        break;
      case MANUAL_PRICE_NAME:
        setManualPrice(value);
        break;
      case MANUAL_RATE_NAME:
        setManualRate(volumeBigToNumber(bigValue));
        break;
      default:
        break;
    }
  };

  const onReleaseCheckboxChange = () => {
    setReleasing(getValues()[RELEASING_CHECKBOX]);
    clearBuyVolumes();
  };

  const clearBuyVolumes = () => {
    setValue(PERCENT_ASKING_NAME, undefined);
    setValue(MW_ASKING_NAME, undefined);
    setValue(MWH_ASKING_NAME, undefined);
    setValue(PRODUCT_CHART_NAME, productQuantity);
    setValue(MANUAL_PRICE_NAME, undefined);
    setValue(MANUAL_RATE_NAME, undefined);
    setPurchaseInfo(undefined);
    setManualPrice(undefined);
    setManualRate(undefined);
  };

  const onProductChange = (productId) => {
    sessionStorage.setItem('buyProduct', productId);
    const changeProduct = findProductById(productId);
    clearBuyVolumes();
    return changeProduct;
  };

  if (productsError || quantitiesError) {
    return <ErrorPage />;
  }
  return (
    <>
      <LoaderHolder>{(productsLoading || quantitiesLoading) && <LoadingIndicator />}</LoaderHolder>
      <BuyLayout>
        <CardRowCard className={'inputAndGraphCard'}>
          <BuyInputs
            register={register}
            onNumberBuyInputChange={onNumberBuyInputChange}
            onProductChange={onProductChange}
            onReleaseCheckboxChange={onReleaseCheckboxChange}
            className={'chart'}
            products={products}
            product={product}
            errors={errors}
            onFillVolumeClicked={onFillVolumeClicked}
            remainingPurchaseCount={remainingPurchaseCount}
            role={user.role}
            isSaleAvailable={isSaleAvailable}
            isFix={contractData.isFix}
            sendNotification={sendNotification}
            setSendNotification={setSendNotification}
          />
          {
            <Controller
              className={'chart'}
              valueName={'data'}
              config={{
                hideYAxesScale: true,
                hideYAxesName: true,
              }}
              defaultValue={productQuantity}
              control={control}
              name={PRODUCT_CHART_NAME}
              as={Chart}
              year={year}
            />
          }
        </CardRowCard>
        <StyledBuySimulationContainer>
          <SectionHeader>
            <FormattedMessage id={`${TRANSLATION_BASE}.simulation.label`} />
          </SectionHeader>
          <CardRow>
            <CardRowCard>
              <Controller
                className={'chart'}
                valueName={'data'}
                defaultValue={purchasePrices}
                control={control}
                name={PURCHASE_SIMULATION_CHART_NAME}
                as={PurchaseSimulation}
                year={year}
              />
            </CardRowCard>
          </CardRow>
        </StyledBuySimulationContainer>
      </BuyLayout>

      <TradeFinishing
        clearBuyVolumes={clearBuyVolumes}
        confirmModalOpen={confirmModalOpen}
        contractData={contractData}
        manualPrice={manualPrice}
        manualRate={manualRate}
        product={product}
        purchaseInfo={purchaseInfo}
        refreshProducts={refreshProducts}
        refreshQuantities={refreshQuantities}
        releasing={releasing}
        reloadContract={reloadContract}
        setConfirmModalOpen={setConfirmModalOpen}
        setValue={setValue}
        showSnackbarGeneralError={showSnackbarGeneralError}
        showSnackbarSuccess={showSnackbarSuccess}
        user={user}
        validate={validate}
        remainingPurchaseCount={remainingPurchaseCount}
        sendNotification={sendNotification}
      />
    </>
  );
};

ContractBuy.propTypes = {
  contractData: PropTypes.object.isRequired,
  getContract: PropTypes.func.isRequired,
};

export default ContractBuy;
